import { formatDayLabel } from '@/lib/dates'
import { callTimeLabel } from './clientMeetings'

export type PrepSection = 'delivered' | 'need' | 'promised' | 'open' | 'helpdesk' | 'unlogged'
export type PrepStage = 'progress' | 'sprint' | 'backlog' | 'triage'
export type SummaryTag = 'lead' | 'chase' | 'good' | 'watch'

export interface PrepRef {
  kind: 'ticket' | 'linear'
  id: string
  ref: string
  url?: string
}

export interface PrepItem {
  key: string
  section: PrepSection
  stage: PrepStage | null
  stateLabel: string | null
  title: string
  refs: PrepRef[]
  person: string | null
  waitingDays: number | null
  openDays: number | null
  due: { kind: 'expected' | 'due' | 'sprint'; date: string } | null
  moment: { seconds: number; url: string } | null
  flag: string | null
  waitingOnUs: boolean
  wording: string | null
  group: string | null
  highlight: string | null
  internal: boolean
  ticked: boolean
}

export interface MeetingPrep {
  nextCallAt: string | null
  postedAt: string | null
  since: string
  lastCall: { _id: string; date: string; title: string } | null
  olderNotLogged: number
  notes: { generatedAt: string; upToDate: boolean; changes: string[]; summary: { tag: SummaryTag; text: string }[]; summaryFailed: boolean } | null
  sections: { section: PrepSection; items: PrepItem[] }[]
}

export const SUMMARY_TAGS: Record<SummaryTag, { label: string; variant: 'info' | 'warning' | 'success' | 'destructive' }> = {
  lead: { label: 'Lead with', variant: 'info' },
  chase: { label: 'Chase', variant: 'warning' },
  good: { label: 'Good news', variant: 'success' },
  watch: { label: 'Watch', variant: 'destructive' },
}

export const STAGE_LABELS: Record<PrepStage, string> = {
  progress: 'In progress or in review',
  sprint: 'In this sprint',
  backlog: 'Backlog',
  triage: 'Triage',
}
export const STAGE_ORDER: PrepStage[] = ['progress', 'sprint', 'backlog', 'triage']

/** The heading each section shows, with "since" read from the last call or the 30-day fallback. */
export function sectionTitle(section: PrepSection, prep: Pick<MeetingPrep, 'since' | 'lastCall'>, clientName: string): string {
  const since = formatDayLabel(prep.since)
  switch (section) {
    case 'delivered':
      return prep.lastCall ? `Delivered since ${since}` : 'Delivered in the last 30 days'
    case 'need':
      return `We need from ${clientName}`
    case 'promised':
      return `Promised on the ${since} call`
    case 'open':
      return 'Still open'
    case 'helpdesk':
      return 'Open help desk tickets'
    case 'unlogged':
      return `Not yet logged from ${since}`
  }
}

export const DUE_PREFIX = { expected: 'Expected', due: 'Due', sprint: 'Sprint ends' } as const

export const itemText = (item: PrepItem) => item.wording ?? item.title

/** The notes as plain text with refs, for pasting into an email or a Teams chat. */
export function prepAsText(prep: MeetingPrep, clientName: string): string {
  const summary = prep.notes?.summary.map(bullet => `- ${SUMMARY_TAGS[bullet.tag].label}: ${bullet.text}`) ?? []
  const line = (item: PrepItem) => {
    const extras = [
      item.refs.map(ref => ref.ref).join(', '),
      item.due ? `${DUE_PREFIX[item.due.kind]} ${formatDayLabel(item.due.date)}` : '',
      item.waitingDays !== null ? `waiting ${item.waitingDays} days` : '',
      item.person ?? '',
      item.moment ? `at ${callTimeLabel(item.moment.seconds)}` : '',
    ].filter(Boolean)
    return `- ${itemText(item)}${extras.length ? ` (${extras.join(', ')})` : ''}`
  }
  const sections = prep.sections
    .filter(section => section.items.length)
    .map(section => [sectionTitle(section.section, prep, clientName), ...section.items.map(line)].join('\n'))
  return [summary.length ? ['Before the call', ...summary].join('\n') : '', ...sections].filter(Boolean).join('\n\n')
}
